import type { DailyQuotas } from './hints';
import { DEFAULT_LANGUAGE, type Language } from './languages';
import { MAX_ROWS } from './lengths';
import { EMPTY_STATS, padDistribution, type Stats } from './stats';
import { KEYS, readJson, scopedKey, writeJson, type StorageAdapter } from './storage';
import { MAX_GUESSES } from './types';

/** Fresh default stats — never the frozen module singleton. */
function freshStats(): Stats {
  return { played: 0, won: 0, distribution: [...EMPTY_STATS.distribution] };
}

/**
 * A stored distribution is valid if it is somewhere between the legacy
 * 6-bucket shape and today's `MAX_ROWS` (spec §10.6). Shorter than 6 was
 * never written by this app and is treated as corruption; longer than
 * MAX_ROWS could only come from a newer build, and resetting is safer
 * than half-reading it.
 */
function isValidStats(v: unknown): v is Stats {
  if (v === null || typeof v !== 'object') return false;
  const s = v as Record<string, unknown>;
  return typeof s.played === 'number'
    && typeof s.won === 'number'
    && Array.isArray(s.distribution)
    && s.distribution.length >= MAX_GUESSES
    && s.distribution.length <= MAX_ROWS
    && s.distribution.every((n) => typeof n === 'number');
}

/**
 * Load aggregate stats; any corrupt or partially-shaped value yields fresh
 * EMPTY_STATS. Stats are per language (`scopedKey`), so a Dutch game never
 * lands in an English distribution.
 */
export async function loadStats(s: StorageAdapter, language: Language = DEFAULT_LANGUAGE): Promise<Stats> {
  const raw = await readJson<unknown>(s, scopedKey(KEYS.stats, language), null);
  if (!isValidStats(raw)) return freshStats();
  return { played: raw.played, won: raw.won, distribution: padDistribution(raw.distribution) };
}

export async function saveStats(
  s: StorageAdapter,
  stats: Stats,
  language: Language = DEFAULT_LANGUAGE,
): Promise<void> {
  await writeJson(s, scopedKey(KEYS.stats, language), stats);
}

function isValidQuotas(v: unknown): v is DailyQuotas {
  if (v === null || typeof v !== 'object') return false;
  const q = v as Record<string, unknown>;
  return typeof q.dateKey === 'string'
    && typeof q.hintsLeft === 'number'
    && typeof q.insightsLeft === 'number';
}

/**
 * Load stored hint/insight quotas; null when absent or corrupt. Composes
 * with `quotasFor(dateKey, stored)`, which treats null the same as a
 * missing or stale budget and returns a fresh one.
 *
 * Deliberately *not* language-scoped: the daily budget belongs to the
 * player and the day, not to a word list (see `scopedKey`).
 */
export async function loadQuotas(s: StorageAdapter): Promise<DailyQuotas | null> {
  const raw = await readJson<unknown>(s, KEYS.quotas, null);
  return isValidQuotas(raw) ? raw : null;
}

export async function saveQuotas(s: StorageAdapter, quotas: DailyQuotas): Promise<void> {
  await writeJson(s, KEYS.quotas, quotas);
}
